import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';

// Format number based on field format configuration
const formatNumber = (value, format, currency = 'USD') => {
  if (value == null || value === '') return '-';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return value;
  
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(numValue);
    case 'decimal':
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue);
    case 'integer':
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0
      }).format(numValue);
    case 'percentage':
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue / 100);
    case 'number':
    default:
      return new Intl.NumberFormat('en-US').format(numValue);
  }
};

// Format date based on field format configuration
const formatDate = (value, format) => {
  if (value == null || value === '') return '-';
  
  let date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  } else {
    return value;
  }
  
  if (isNaN(date.getTime())) return value;
  
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  switch (format) {
    case 'relative':
      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) !== 1 ? 's' : ''} ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) !== 1 ? 's' : ''} ago`;
      return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) !== 1 ? 's' : ''} ago`;
    case 'datetime':
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    case 'iso':
      return date.toISOString().split('T')[0];
    case 'iso-datetime':
      return date.toISOString().replace('T', ' ').split('.')[0];
    case 'long':
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    case 'short':
      return new Intl.DateTimeFormat('en-US', {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric'
      }).format(date);
    case 'date':
    default:
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
  }
};

// Check if field type is numeric
const isNumericField = (type) => {
  const numericTypes = ['integer', 'decimal', 'number', 'int', 'float', 'double', 'bigint', 'currency'];
  return numericTypes.includes(type?.toLowerCase());
};

// Check if field type is date
const isDateField = (type) => {
  const dateTypes = ['date', 'datetime', 'timestamp'];
  return dateTypes.includes(type?.toLowerCase());
};

// Format cell value based on field configuration
const formatCellValue = (value, field) => {
  if (value == null || value === '') return '-';
  
  if (isNumericField(field.type) && field.format) {
    return formatNumber(value, field.format, field.currency);
  }
  
  if (isDateField(field.type) && field.format) {
    return formatDate(value, field.format);
  }
  
  return String(value);
};

const TableDisplay = ({ component, customization = {} }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  // Parse config once
  const config = typeof component.ConfigJSON === 'string' 
    ? JSON.parse(component.ConfigJSON) 
    : component.ConfigJSON || component.config || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(config.maxRecords || 100); // Use config.maxRecords or default to 100
  const [sortBy, setSortBy] = useState(config.sort?.field || null); // Initialize from config
  const [sortOrder, setSortOrder] = useState(config.sort?.direction?.toUpperCase() || 'ASC'); // Initialize from config

  useEffect(() => {
    loadData();
  }, [component.ComponentID, page, sortBy, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/visualization-components/${component.ComponentID}/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page,
          limit,
          sortBy,
          sortOrder
        })
      });

      if (!response.ok) {
        throw new Error('Failed to load data');
      }

      const result = await response.json();
      setData(result.data);
    } catch (err) {
      console.error('Error loading table data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (fieldName) => {
    if (sortBy === fieldName) {
      // Toggle sort order
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(fieldName);
      setSortOrder('ASC');
    }
    setPage(1); // Reset to first page when sorting
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data?.pagination && page < data.pagination.totalPages) {
      setPage(page + 1);
    }
  };

  const fields = config.fields || [];

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-red-200 dark:border-red-800">
        <div className="text-red-600 dark:text-red-400 text-sm">
          Error loading data
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          {customization.title || component.Name}
        </h3>
        <button
          onClick={loadData}
          disabled={loading}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">Loading...</p>
          </div>
        ) : !data || !data.rows || data.rows.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            No data available
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {fields.map(field => (
                  <th
                    key={field.name}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => handleSort(field.name)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{field.label}</span>
                      {sortBy === field.name ? (
                        sortOrder === 'ASC' ? (
                          <ArrowUp className="w-4 h-4" />
                        ) : (
                          <ArrowDown className="w-4 h-4" />
                        )
                      ) : (
                        <ArrowUpDown className="w-4 h-4 text-gray-300 dark:text-gray-500" />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {data.rows.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  {fields.map(field => (
                    <td
                      key={field.name}
                      className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap"
                    >
                      {formatCellValue(row[field.name], field)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, data.pagination.total)} of {data.pagination.total} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {page} of {data.pagination.totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={page === data.pagination.totalPages}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableDisplay;

